import {
  IAssistantMessage,
  IQuestion,
  IQuestionOption,
  ISurgeItem,
  IWindowCartItem,
} from "@/features/assistant/types";
import {
  FOIL_COLOUR_OPTION_TYPES,
  MESSAGE_TYPE_ENUM,
  OPTION_TYPE_SPRAY,
} from "@/features/assistant/constants";
import { isProfileColourFoilOption } from "@/features/assistant/utils/profileColour";
import { Obj } from "@/types";

const roundToTwoDecimals = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Detects a mobile-app-local cache path (e.g.
 * "file:///var/mobile/Containers/Data/Application/<uuid>/Documents/template-cache/<id>.png")
 * stored verbatim in a sub-product's serialized option (`dogma_valuejson`).
 * These paths only resolve on the device that created them — the web app has
 * no filesystem access to them, so any image load (including grill blending,
 * which reads an option's `thumbnail` directly) fails silently/loudly.
 */
const isMobileLocalImagePath = (url?: string | null): boolean => {
  if (!url) return false;
  return (
    url.startsWith("file://") ||
    url.includes("/template-cache/") ||
    url.includes("/Containers/Data/Application/")
  );
};

/**
 * Reverses margin to recover the pre-margin ("original") customer price from
 * a margin-baked amount, so a quote's margin can be changed later without
 * re-running the whole assistant flow. Margin is a share of the selling
 * price (amount = cost / (1 - margin/100)), so reversing it is a straight
 * multiply: cost = amount * (1 - margin/100) — not an assumption that
 * original === vssPrice, since it stays correct even where price isn't
 * purely proportional to vss (flat charges, independent per-field rounding).
 */
const deriveOriginalPrice = (
  amount: number,
  marginPercentage?: number,
): number => {
  if (!amount || !marginPercentage) return amount;
  return roundToTwoDecimals(amount * (1 - marginPercentage / 100));
};

/**
 * Returns the VSS discount to persist for a line, or `undefined` when none
 * should be stored. A negative VSS price (a deduction-style option such as
 * "Both Sashes Fixed") must never carry a discount: the config's discount %
 * is baked onto the negative VSS price too (e.g. -24 × 10% = -2.40), and
 * downstream netting (`vssPrice - vssdiscountamount` → -24 - (-2.40) = -21.60)
 * would erode the credit toward zero. Persisting no discount keeps the full
 * negative amount intact on the VSS track.
 */
const resolveStoredVssDiscount = (
  vssPrice: number,
  discount?: number,
): number | undefined => {
  if (vssPrice < 0) return undefined;
  return discount || undefined;
};

export interface ISubQuoteProduct {
  dogma_name: string;
  dogma_amount: number;
  dogma_vssprice?: number;
  dogma_vssdiscountamount?: number;
  dogma_originalprice?: number;
  dogma_vssoriginalprice?: number;
  "dogma_QuoteProduct@odata.bind": string;
  dogma_quantity: number;
  dogma_step?: number;
  dogma_optionname?: string;
  dogma_categoryname?: string;
  dogma_value?: string;
  dogma_valuejson?: string;
  dogma_questionjson?: string;
}

/**
 * Builds a single ISubQuoteProduct for a pre-quote extra (Fitting Type, Fixing Lugs).
 * Constructs dogma_valuejson and dogma_questionjson in the same format used by
 * transformAToSubQuoteProducts so reconstructAssistantMessages can parse them.
 */
export const buildPreQuoteSubProduct = (params: {
  question: IQuestion;
  option: IQuestionOption;
  quotedetailid: string;
  name: string;
  amount: number;
  vssAmount: number;
  quantity: number;
  step: number;
  optionname: string;
  categoryname: string;
  marginPercentage?: number;
}): ISubQuoteProduct => {
  const assistantMessage: IAssistantMessage = {
    id: `pre_quote_${params.question.questionId}_${Date.now()}`,
    type: MESSAGE_TYPE_ENUM.assistant as IAssistantMessage["type"],
    message: params.question.question,
    question: params.question,
    response: {
      answer: [params.option],
      selectedOptionsIds: [params.option.optionId],
      form: {},
    },
  };

  return {
    dogma_name: params.name,
    dogma_amount: params.amount,
    dogma_vssprice: params.vssAmount,
    dogma_originalprice: deriveOriginalPrice(
      params.amount,
      params.marginPercentage,
    ),
    dogma_vssoriginalprice: params.vssAmount,
    "dogma_QuoteProduct@odata.bind": `/quotedetails(${params.quotedetailid})`,
    dogma_quantity: params.quantity,
    dogma_step: params.step,
    dogma_optionname: params.optionname,
    dogma_categoryname: params.categoryname,
    dogma_valuejson: JSON.stringify(params.option),
    dogma_questionjson: JSON.stringify(assistantMessage),
  };
};

export const transformAToSubQuoteProducts = (
  cartItems: IWindowCartItem,
  quoteDetailIds: string,
  marginPercentage?: number,
): ISubQuoteProduct[] => {
  const subQuoteProducts: ISubQuoteProduct[] = [];
  const processedQuestions = new Set<string>(); // Track processed questions to avoid duplicates

  // Filter to get user and price-surge messages (exclude assistant messages)
  const userAndSurgeMessages = cartItems.messages.filter(
    (msg) =>
      msg.type === MESSAGE_TYPE_ENUM.user ||
      msg.type === MESSAGE_TYPE_ENUM.priceSurge,
  );
  // Check if there's a variation (optionType 64) in messages
  const hasVariation = userAndSurgeMessages.some(
    (msg) => msg.question?.optionType === 64,
  );

  // Spray+Foil: when spray=Yes and foil profile selected, zero foil colour amounts
  const isFoilProfileTypeSelected = userAndSurgeMessages.some(
    (m) =>
      m.question?.optionType === 60 &&
      m.response?.answer?.some((a: any) =>
        isProfileColourFoilOption(a.responseValue ?? ""),
      ),
  );
  const isSprayYesSelected = userAndSurgeMessages.some(
    (m) =>
      m.question?.optionType === OPTION_TYPE_SPRAY &&
      m.response?.answer?.some((a: any) =>
        (a.responseValue ?? a.optionText ?? "").toLowerCase().includes("yes"),
      ),
  );
  const shouldZeroFoilColour = isFoilProfileTypeSelected && isSprayYesSelected;

  // Build lookup maps from the stored IPriceBreakdownList.
  // breakdown.price is already dealerPrice × unit (the whole amount), so we
  // store that directly as dogma_amount and always save dogma_quantity = 1.
  //
  // Three lookup strategies, most-specific first:
  //  1. optionId  — globally unique; populated for breakdowns saved after the
  //                 optionId field was added to IPriceBreakdown.
  //  2. composite — "optionName|label" (e.g. "Run-Through Horns|Yes"); unique
  //                 per question type; works for ALL existing stored breakdowns
  //                 because optionName + label are always persisted.
  //  3. label     — kept only for surge items that carry no optionName.
  //
  // Surge items have neither a distinct optionId nor a distinct optionName —
  // a per-frame threshold surcharge firing twice (e.g. two independently
  // oversized frames) produces two breakdown entries with the IDENTICAL
  // optionName/label ("Dimension" / "Oversized Width Charge"), so even the
  // composite lookup collides. The *ByLabel maps therefore store an ARRAY per
  // label and are consulted positionally (1st surgeItem of that ruleName ↔
  // 1st breakdown entry, 2nd ↔ 2nd) — a plain single-value Map would make
  // every duplicate surgeItem resolve to the SAME one breakdown entry's
  // amount, silently discarding the others' real values.
  const lineTotalByOptionId = new Map<string, number>();
  const vssTotalByOptionId = new Map<string, number>();
  const vssDiscountByOptionId = new Map<string, number>();
  const lineTotalByComposite = new Map<string, number>();
  const vssTotalByComposite = new Map<string, number>();
  const vssDiscountByComposite = new Map<string, number>();
  const lineTotalByLabel = new Map<string, number[]>();
  const vssTotalByLabel = new Map<string, number[]>();
  const vssDiscountByLabel = new Map<string, number[]>();
  (cartItems.price?.breakdown ?? []).forEach((item) => {
    if (item.optionId) {
      lineTotalByOptionId.set(item.optionId, item.price ?? 0);
      vssTotalByOptionId.set(item.optionId, item.vssPrice ?? 0);
      vssDiscountByOptionId.set(item.optionId, item.vssDiscountAmount ?? 0);
    }
    const compositeKey = `${item.optionName}|${item.label}`;
    lineTotalByComposite.set(compositeKey, item.price ?? 0);
    vssTotalByComposite.set(compositeKey, item.vssPrice ?? 0);
    vssDiscountByComposite.set(compositeKey, item.vssDiscountAmount ?? 0);
    if (!lineTotalByLabel.has(item.label)) lineTotalByLabel.set(item.label, []);
    if (!vssTotalByLabel.has(item.label)) vssTotalByLabel.set(item.label, []);
    if (!vssDiscountByLabel.has(item.label))
      vssDiscountByLabel.set(item.label, []);
    lineTotalByLabel.get(item.label)!.push(item.price ?? 0);
    vssTotalByLabel.get(item.label)!.push(item.vssPrice ?? 0);
    vssDiscountByLabel.get(item.label)!.push(item.vssDiscountAmount ?? 0);
  });

  // Global (not per-message) occurrence counter — surge items for the same
  // ruleName can be split across multiple price-surge messages at different
  // steps, so this must walk the breakdown's array in overall order.
  const labelOccurrence = new Map<string, number>();

  userAndSurgeMessages.forEach((message) => {
    // Find the preceding assistant message for this message in the full messages array.
    // Auto-injected synthetic messages (e.g. buildCouplingClipMessage) have no bot
    // bubble of their own, so a blind backward scan can misattribute them to
    // whatever real question happens to precede them — corrupting dogma_questionjson
    // and, on reload, merging the synthetic answer into that unrelated question's
    // group. Only trust the scanned assistant message when it actually asked the
    // same question; otherwise synthesize one from the message's own question.
    const messageIndex = cartItems.messages.indexOf(message);
    let precedingAssistantMessage: IAssistantMessage | undefined;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (cartItems.messages[i]?.type === MESSAGE_TYPE_ENUM.assistant) {
        precedingAssistantMessage = cartItems.messages[i];
        break;
      }
    }
    const matchesOwnQuestion =
      !!precedingAssistantMessage?.question?.questionId &&
      precedingAssistantMessage.question.questionId === message.question?.questionId;
    const questionJson = matchesOwnQuestion
      ? JSON.stringify(precedingAssistantMessage)
      : message.question
        ? JSON.stringify({
            id: `synthetic_${message.id}`,
            type: MESSAGE_TYPE_ENUM.assistant as IAssistantMessage["type"],
            message: message.question.question,
            question: message.question,
            response: { answer: [], selectedOptionsIds: [], form: {} },
          } as IAssistantMessage)
        : undefined;

    // Handle price-surge messages first (they have a different structure)
    if (message.type === MESSAGE_TYPE_ENUM.priceSurge && message.surgeItems) {
      message.surgeItems.forEach((surgeItem: any) => {
        const occurrenceIndex = labelOccurrence.get(surgeItem.ruleName) ?? 0;
        labelOccurrence.set(surgeItem.ruleName, occurrenceIndex + 1);
        const fullVss =
          vssTotalByLabel.get(surgeItem.ruleName)?.[occurrenceIndex] ??
          surgeItem.vssSurgeAmount ??
          0;
        const fullVssDiscount =
          vssDiscountByLabel.get(surgeItem.ruleName)?.[occurrenceIndex] ??
          surgeItem.vssDiscountAmount ??
          0;
        let fullAmount =
          lineTotalByLabel.get(surgeItem.ruleName)?.[occurrenceIndex] ??
          surgeItem.surgeAmount ??
          0;
        // The customer-facing amount can be floored to 0 when a discount fully
        // offsets a subtract rule, while the VSS track keeps the negative. Preserve
        // the negative so dogma_amount stores the same signed value as dogma_vssprice.
        if (
          fullAmount === 0 &&
          (surgeItem.surgeAmount ?? 0) === 0 &&
          fullVss < 0
        ) {
          fullAmount = fullVss;
        }
        subQuoteProducts.push({
          dogma_name: surgeItem.ruleName || "Surge Charge",
          dogma_amount: fullAmount,
          dogma_vssprice: fullVss,

          dogma_originalprice: deriveOriginalPrice(
            fullAmount,
            marginPercentage,
          ),
          dogma_vssoriginalprice: fullVss,
          dogma_vssdiscountamount: resolveStoredVssDiscount(fullVss, fullVssDiscount),
          "dogma_QuoteProduct@odata.bind": `/quotedetails(${quoteDetailIds})`,
          dogma_quantity: 1,
          dogma_step: message.question?.step,
          dogma_optionname: "Surcharge",
          dogma_categoryname: "Additional Charges",
          dogma_valuejson: JSON.stringify(surgeItem),
          dogma_questionjson: questionJson,
        });
      });
      return; // Skip regular processing for surge messages
    }

    // Handle regular user messages
    const question = message.question;
    const response = message.response;

    // Skip if no question or response
    if (!question || !response) return;

    // Skip FINAL_ACTION category
    if (question.questionId === "Q0126_FINAL") {
      return;
    }

    // Skip if we've already processed this question
    if (processedQuestions.has(question.questionId)) {
      return;
    }

    // Mark this question as processed
    processedQuestions.add(question.questionId);

    // Process each answer in the response
    if (response.answer && Array.isArray(response.answer)) {
      response.answer.forEach((answerOption) => {
        // When a variation (optionType 64) exists, skip product UUID answers from
        // optionType-0 questions (they are superseded by the variation).
        // Category answers with short optionIds (e.g. "A0002" for Standard Window)
        // are always preserved — they drive display conditions for later questions.
        if (
          question.optionType === 0 &&
          hasVariation &&
          answerOption.optionId.length > 7
        ) {
          return;
        }

        // Use optionText as the name
        const displayName = answerOption.optionText || "Sub Product";

        // Resolve the full baked line total from the price breakdown.
        // 1. optionId lookup — exact match, collision-free (new breakdowns).
        // 2. composite key  — "optionTypeName|optionText", unique per question
        //                     type; works for all existing stored breakdowns.
        // 3. raw fallback   — priceModifier preferred over price (mirrors
        //                     resolvePriceListPrice: priceModifier is the
        //                     priceList-resolved value, price is the base).
        const compositeKey = `${answerOption.optionTypeName}|${displayName}`;
        const rawPrice =
          (answerOption.priceModifier ?? 0) !== 0
            ? answerOption.priceModifier!
            : (answerOption.price ?? 0);

        // Per Unit options (optionUnit 4): always re-derive from live form value × unit price.
        // The stored breakdown can be stale if the user updated the quantity after the item
        // was first saved, which would cause the old price to leak into the subproduct.
        const isPerUnit = (answerOption as any).optionUnit === 4;
        const perUnitQty =
          isPerUnit && answerOption.responseValue
            ? Math.max(
                0,
                Number(response.form?.[answerOption.responseValue] ?? 0),
              )
            : null;

        const fullAmount =
          perUnitQty !== null
            ? rawPrice * perUnitQty
            : (lineTotalByOptionId.get(answerOption.optionId) ??
              lineTotalByComposite.get(compositeKey) ??
              rawPrice);
        const fullVss =
          perUnitQty !== null
            ? (answerOption.vssPrice ?? 0) * perUnitQty
            : (vssTotalByOptionId.get(answerOption.optionId) ??
              vssTotalByComposite.get(compositeKey) ??
              answerOption.vssPrice ??
              0);
        // Per Unit (optionUnit 4) discount: the breakdown map lookup doesn't apply here —
        // the breakdown's vssDiscountAmount for a per-unit item is already baked to a
        // specific quantity at calc time, which can be stale vs. the live form quantity
        // (same staleness concern as fullAmount/fullVss above). Recompute straight from
        // the option's own discountPercentage/discountAmount/vssDiscountAmount, scaled by
        // the live quantity — same per-unit discount the option carries into the live chat
        // breakdown (configurationPriceCalculation.ts), just persisted here too.
        const fullVssDiscount =
          perUnitQty !== null
            ? (answerOption.discountPercentage ?? 0) > 0
              ? roundToTwoDecimals(
                  (answerOption.vssPrice ?? 0) *
                    (answerOption.discountPercentage! / 100),
                ) * perUnitQty
              : (answerOption.discountAmount ?? 0) > 0
                ? (answerOption.discountAmount ?? 0) * perUnitQty
                : (answerOption.vssDiscountAmount ?? 0) * perUnitQty
            : (vssDiscountByOptionId.get(answerOption.optionId) ??
              vssDiscountByComposite.get(compositeKey) ??
              0);

        // Spray+Foil: zero amount for internal/external foil colour items when spray=Yes
        const isFoilColourItem =
          shouldZeroFoilColour &&
          FOIL_COLOUR_OPTION_TYPES.has(question.optionType);
        const effectiveAmount = isFoilColourItem ? 0 : fullAmount;
        const effectiveVss = isFoilColourItem ? 0 : fullVss;

        // Check if this is a form input and get the actual value
        const formValues = response.form;
        const isFormValid =
          formValues &&
          typeof formValues === "object" &&
          Object.keys(formValues).length > 0 &&
          answerOption.responseValue;

        if (isFormValid && formValues) {
          const formValue = formValues[answerOption.responseValue];
          subQuoteProducts.push({
            dogma_name: displayName,
            dogma_amount: effectiveAmount,
            dogma_vssprice: effectiveVss,

            dogma_originalprice: deriveOriginalPrice(
              effectiveAmount,
              marginPercentage,
            ),
            dogma_vssoriginalprice: effectiveVss,
            dogma_vssdiscountamount: resolveStoredVssDiscount(effectiveVss, fullVssDiscount),
            "dogma_QuoteProduct@odata.bind": `/quotedetails(${quoteDetailIds})`,
            dogma_quantity: 1,
            dogma_step: question.step,
            dogma_optionname: question.optionTypeName || "",
            dogma_categoryname:
              question.categoryName || question.category || "Configuration",
            dogma_value: formValue ? `${formValue}` : " ",
            dogma_valuejson: JSON.stringify(answerOption),
            dogma_questionjson: questionJson,
          });
        } else {
          subQuoteProducts.push({
            dogma_name: displayName,
            dogma_amount: effectiveAmount,
            dogma_vssprice: effectiveVss,

            dogma_originalprice: deriveOriginalPrice(
              effectiveAmount,
              marginPercentage,
            ),
            dogma_vssoriginalprice: effectiveVss,
            dogma_vssdiscountamount: resolveStoredVssDiscount(effectiveVss, fullVssDiscount),
            "dogma_QuoteProduct@odata.bind": `/quotedetails(${quoteDetailIds})`,
            dogma_quantity: 1,
            dogma_step: question.step,
            dogma_optionname: question.optionTypeName || "",
            dogma_categoryname:
              question.categoryName || question.category || "Configuration",
            dogma_valuejson: JSON.stringify(answerOption),
            dogma_questionjson: questionJson,
          });
        }

        // Create sub product for each answer option
      });
    }
  });

  return subQuoteProducts;
};

/**
 * Reconstructs the original IAssistantMessage[] from sub quote products.
 * Relies on dogma_questionjson (serialised assistant message) and
 * dogma_valuejson (serialised IQuestionOption) that were stored during
 * transformAToSubQuoteProducts.
 *
 * Output order mirrors the original conversation:
 *   [assistant, user, (price-surge)?, assistant, user, ...]
 */
export const reconstructAssistantMessages = (
  subQuoteProducts: ISubQuoteProduct[],
  questionFlow: IQuestion[] = [],
): IAssistantMessage[] => {
  type Group = {
    // Best available assistant message JSON for this group
    questionJson: string;
    isSurge: boolean;
    products: ISubQuoteProduct[];
  };

  // Current config's options by id — used to replace a stored option's
  // thumbnail when it's a mobile-local cache path (see isMobileLocalImagePath)
  // that the web app can never load, with the live, web-accessible URL for
  // that same option from the current config.
  const configOptionById = new Map<string, IQuestionOption>();
  questionFlow.forEach((q) => {
    q.options?.forEach((o) => {
      if (o.optionId) configOptionById.set(o.optionId, o);
    });
  });
  const resolveThumbnail = (opt: IQuestionOption): IQuestionOption => {
    if (!isMobileLocalImagePath(opt.thumbnail)) return opt;
    const configOpt = opt.optionId
      ? configOptionById.get(opt.optionId)
      : undefined;
    return configOpt?.thumbnail
      ? { ...opt, thumbnail: configOpt.thumbnail }
      : opt;
  };

  // Sort by step first so the conversation order is always correct
  // regardless of the order the API returns records.
  const sorted = [...subQuoteProducts].sort(
    (a, b) => (a.dogma_step ?? 0) - (b.dogma_step ?? 0),
  );

  // Extract questionId from stored assistant message JSON so that sub-products
  // created across different edit sessions (different message IDs, same questionId)
  // are merged into a single group instead of being split.
  const extractQuestionId = (questionJson: string): string | null => {
    if (!questionJson) return null;
    try {
      const msg = JSON.parse(questionJson) as IAssistantMessage;
      return msg.question?.questionId ?? null;
    } catch {
      return null;
    }
  };

  // Group by questionId (preferred) so all sub-products for the same question
  // are merged even when they carry assistant messages with different IDs.
  // Surge items are bucketed per step to preserve their position.
  const orderedKeys: string[] = [];
  const groupsByKey = new Map<string, Group>();

  sorted.forEach((product) => {
    const isSurge = product.dogma_optionname === "Surcharge";
    const questionJson = product.dogma_questionjson ?? "";
    const questionId = extractQuestionId(questionJson);

    const key = isSurge
      ? `surge__${product.dogma_step ?? 0}`
      : questionId
        ? `q__${questionId}`
        : `step__${product.dogma_step ?? 0}`;

    if (!groupsByKey.has(key)) {
      orderedKeys.push(key);
      groupsByKey.set(key, { questionJson, isSurge, products: [product] });
    } else {
      const group = groupsByKey.get(key)!;
      group.products.push(product);
      // Prefer the questionJson with more content (more likely to have full options list)
      if (questionJson.length > group.questionJson.length) {
        group.questionJson = questionJson;
      }
    }
  });

  const messages: IAssistantMessage[] = [];
  const addedQuestionIds = new Set<string>();

  orderedKeys.forEach((key) => {
    const { questionJson, isSurge, products } = groupsByKey.get(key)!;
    const questionId = extractQuestionId(questionJson);

    // Add the preceding assistant message once per questionId
    if (questionJson && questionId && !addedQuestionIds.has(questionId)) {
      try {
        const assistantMsg = JSON.parse(questionJson) as IAssistantMessage;
        messages.push(assistantMsg);
        addedQuestionIds.add(questionId);
      } catch {
        // malformed json — skip
      }
    } else if (questionJson && !questionId) {
      // No questionId extractable — fall back to adding by raw JSON (legacy data)
      try {
        const assistantMsg = JSON.parse(questionJson) as IAssistantMessage;
        messages.push(assistantMsg);
      } catch {
        // malformed json — skip
      }
    }

    if (isSurge) {
      // Reconstruct price-surge message
      const surgeItems: ISurgeItem[] = products.map((p) => {
        if (p.dogma_valuejson) {
          try {
            return JSON.parse(p.dogma_valuejson) as ISurgeItem;
          } catch {}
        }
        return {
          ruleId: "",
          ruleName: p.dogma_name,
          surgeAmount: p.dogma_amount,
          vssSurgeAmount: p.dogma_vssprice ?? 0,
          operation: "add",
        };
      });

      messages.push({
        type: MESSAGE_TYPE_ENUM.priceSurge as IAssistantMessage["type"],
        message: "Price adjustments applied",
        question: null as unknown as IQuestion,
        response: { answer: [], selectedOptionsIds: [], form: {} },
        id: crypto.randomUUID(),
        surgeItems,
      });
    } else {
      // Reconstruct user message
      let question: IQuestion | null = null;
      try {
        question =
          (JSON.parse(questionJson) as IAssistantMessage).question ?? null;
        if (question?.options?.length) {
          question = {
            ...question,
            options: question.options.map(resolveThumbnail),
          };
        }
      } catch {}

      const answers: IQuestionOption[] = [];
      const selectedOptionsIds: string[] = [];
      const form: Obj = {};

      products.forEach((p) => {
        if (!p.dogma_valuejson) return;
        try {
          const opt = resolveThumbnail(
            JSON.parse(p.dogma_valuejson) as IQuestionOption,
          );
          answers.push(opt);
          if (opt.optionId) selectedOptionsIds.push(opt.optionId);
          // Restore the form value that was written into dogma_value
          if (p.dogma_value?.trim() && opt.responseValue) {
            form[opt.responseValue] = p.dogma_value.trim();
          }
        } catch {}
      });

      messages.push({
        type: MESSAGE_TYPE_ENUM.user as IAssistantMessage["type"],
        message: products.map((p) => p.dogma_name).join(", "),
        question: question!,
        response: { answer: answers, selectedOptionsIds, form },
        id: crypto.randomUUID(),
      });
    }
  });

  return messages;
};
